import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import ConfigurableTable from '@uikit/old/ConfigurableTable/ConfigurableTable';
import Loader from '@uikit/old/Loader/Loader';
import NoAccessMessage from '@uikit/old/NoAccessMessage/NoAccessMessage';
import Pagination from '@uikit/old/Pagination/Pagination';
import usePagination from '@hooks/usePagination';
import useNonInitialEffect from '@hooks/useNonInitialEffect';

import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import queryCacheKeys from '@constants/queryCacheKeys.constants';

import {
  changePublisherOrdersStatusApi,
  getPublisherOrdersApi,
  getPublisherWebsitesApi,
  updateOrderPublishedUrlApi,
} from '@api/publishers.api';

import * as common_pb from 'respona_api/generated/common_pb';
import { PublisherOrderStatus } from 'respona_api/generated/common_pb';
import BaseModal from '@uikit/Modals/BaseModal/BaseModal';
import Input from '@uikit/Form/Input/Input';
import { Button } from '@uikit/Form/Button/Button';
import Textarea from '@uikit/old/Textarea/Textarea';
import {
  ChangeRequestType,
  PublishersOrderFilterType,
  PublishersOrderType,
} from '@ts/publishers.types';

import './OrdersTable.scss';
import StubIllustration from '@uikit/old/StubIllustration/StubIllustration';
import OrdersIllustration from '@uikit/old/StubIllustration/_components/OrdersIllustration';
import { fetchPublisherBillingDetails } from '@redux/thunks/billings-publisher.thunks';
import { PublisherOrderFilterFieldMap } from 'respona_api/generated/order-publisher_pb';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import { addNotification } from '@redux/actions/notifications.actions';
import { expandHiddenPublisherOrderStatusFilters } from '@constants/filters/publisherOrderFilterFields';
import { FilterFieldType } from '@ts/filterField.type';
import ChangeRequestSidebar from '@components/Publisher/Orders/OrdersTable/_components/ChangeRequestSidebar/ChangeRequestSidebar';
import InsertionBrief from '@components/shared/InsertionBrief/InsertionBrief';
import {
  getInsertionBriefUrl,
  isLinkInsertionOrder,
} from '@components/Publisher/Orders/OrdersTable/utils';
import {
  INSERTION_BRIEF_COPY,
  INVALID_LIVE_URL_ERROR,
} from '@components/Publisher/Orders/OrdersTable/constants';
import { normalizeWebUrl } from '@utils/url.utils';
import tableConfig, { RenderRow } from './tableConfig';

type ActivePrompt =
  | { type: 'feedback'; articleId: number }
  | { type: 'add-url'; articleId: number }
  | { type: 'edit-url'; articleId: number; currentUrl: string };

function OrdersTable({
  appliedFilters,
}: {
  // Filter *state*, not the wire shape: dropdown values are still `{label, value}`
  // objects at this point and get unwrapped in the api layer.
  appliedFilters: FilterFieldType<PublisherOrderFilterFieldMap>[];
}): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [activePrompt, setActivePrompt] = useState<ActivePrompt | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [publishedUrl, setPublishedUrl] = useState('');
  const [publishedUrlError, setPublishedUrlError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isViewChangeRequestSidebarOpen, setIsViewChangeRequestSidebarOpen] = useState(false);
  // Row id, not a row snapshot: the sidebar reads the live row out of the cache
  // below, so a status change patched into the list shows up in the sidebar too.
  const [viewChangeRequestRowId, setViewChangeRequestRowId] = useState<number>(null);
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const queryClient = useQueryClient();
  const { page, changePage, limit, changeLimit, changeToDefault } = usePagination();
  const ordersQueryKey = [queryCacheKeys.orders, workspaceId, page - 1, limit, appliedFilters];

  const {
    isLoading: isOrdersLoading,
    data: orders = [],
    error: ordersError,
    refetch: refetchOrders,
  } = useQuery(
    ordersQueryKey,
    () => {
      const expandedFilters = expandHiddenPublisherOrderStatusFilters(appliedFilters).map(
        (f, idx) => ({ ...f, order: idx })
      );
      return getPublisherOrdersApi(
        workspaceId,
        page - 1,
        limit,
        expandedFilters as unknown as PublishersOrderFilterType[]
      );
    },
    {
      enabled: !!workspaceId,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 1000 * 60, // 5 minutes
    }
  );

  const { data: websites = [], isFetching: isWebsitesFetching } = useQuery(
    [queryCacheKeys.publisherWebsites, workspaceId, 0, 10, []],
    () => getPublisherWebsitesApi(workspaceId, 0, 10, []),
    {
      enabled: !!workspaceId,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 1000 * 60,
    }
  );

  useNonInitialEffect(() => {
    if (workspaceId) {
      refetchOrders();
      changeToDefault();
    }
  }, [workspaceId, appliedFilters]);

  useNonInitialEffect(() => {
    if (workspaceId) {
      refetchOrders();
    }
  }, [page, limit]);

  // The feedback / reject prompt: a link insertion is an order to decline, not an article.
  const feedbackOrder =
    activePrompt?.type === 'feedback'
      ? (orders.find(({ id }) => id === activePrompt.articleId) ?? null)
      : null;
  const feedbackSubject = isLinkInsertionOrder(feedbackOrder) ? 'order' : 'article';

  const handleChangeStatus = (
    articleId: number,
    status: common_pb.PublisherOrderStatusMap[keyof common_pb.PublisherOrderStatusMap]
  ) => {
    if (status === PublisherOrderStatus.PUBLISHER_ORDER_REJECTED) {
      setRejectReason('');
      setActivePrompt({ type: 'feedback', articleId });
      return;
    }
    if (status === PublisherOrderStatus.PUBLISHER_ORDER_PENDING_LIVE) {
      setPublishedUrl('');
      setActivePrompt({ type: 'add-url', articleId });
      return;
    }

    const delay = 6000;
    // The request layer already shows the server's refusal as an error toast (an order the team
    // withdrew while this tab was open, say). Refetch either way so the row shows the real status,
    // and never leave the rejection unhandled (RES-7342).
    const timer = setTimeout(() => {
      changePublisherOrdersStatusApi(articleId, workspaceId, status)
        .catch(() => undefined)
        .finally(() => refetchOrders());
    }, delay);

    let title = 'Link is going to be approved';
    if (status === PublisherOrderStatus.PENDING_PUBLICATION) {
      const acceptedOrder = orders.find(({ id }) => id === articleId);
      // A link insertion has no article to publish: the publisher follows the
      // brief (when there is one) and submits the URL of their own article.
      if (getInsertionBriefUrl(acceptedOrder)) {
        title =
          'Order accepted. Insert the link as described in the brief, then enter the live URL.';
      } else if (isLinkInsertionOrder(acceptedOrder)) {
        title = 'Order accepted. Please enter the live URL.';
      } else {
        title = 'Article accepted. Please enter the live URL.';
      }
    }

    dispatch(
      addNotification({
        title,
        type: 'warning',
        onDecline: () => clearTimeout(timer),
        onDeclineText: 'UNDO',
        delay,
      })
    );
  };

  const handleEditUrl = (articleId: number, currentUrl: string) => {
    setPublishedUrl(currentUrl ?? '');
    setActivePrompt({ type: 'edit-url', articleId, currentUrl: currentUrl ?? '' });
  };

  const closePrompt = () => {
    setActivePrompt(null);
    setRejectReason('');
    setPublishedUrl('');
    setPublishedUrlError('');
  };

  const submitFeedback = async (
    status:
      | common_pb.PublisherOrderStatusMap['REVISION_REQUESTED']
      | common_pb.PublisherOrderStatusMap['PUBLISHER_ORDER_REJECTED']
  ) => {
    if (activePrompt?.type !== 'feedback') return;
    const { articleId } = activePrompt;

    if (status === PublisherOrderStatus.PUBLISHER_ORDER_REJECTED) {
      const delay = 6000;
      const feedback = rejectReason;
      const currentWorkspaceId = workspaceId;
      const timer = setTimeout(() => {
        changePublisherOrdersStatusApi(articleId, currentWorkspaceId, status, null, feedback)
          .catch(() => undefined)
          .finally(() => refetchOrders());
      }, delay);

      dispatch(
        addNotification({
          title: feedbackSubject === 'order' ? 'Order is rejected' : 'Article is rejected',
          type: 'warning',
          onDecline: () => clearTimeout(timer),
          onDeclineText: 'UNDO',
          delay,
        })
      );
      closePrompt();
      return;
    }

    setIsVerifying(true);
    try {
      await changePublisherOrdersStatusApi(articleId, workspaceId, status, null, rejectReason);
    } catch (error) {
      console.error(error);
    }
    setIsVerifying(false);
    closePrompt();
    refetchOrders();
  };

  const onPublishedUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPublishedUrl(event.target.value);
    setPublishedUrlError('');
  };

  const submitNewUrl = async () => {
    if (activePrompt?.type !== 'add-url') return;
    const { articleId } = activePrompt;
    const liveUrl = normalizeWebUrl(publishedUrl);
    if (!liveUrl) {
      setPublishedUrlError(INVALID_LIVE_URL_ERROR);
      return;
    }

    setIsVerifying(true);
    try {
      await changePublisherOrdersStatusApi(
        articleId,
        workspaceId,
        PublisherOrderStatus.PUBLISHER_ORDER_PENDING_LIVE,
        liveUrl
      );
      dispatch(fetchPublisherBillingDetails(true));
    } catch (error) {
      console.error(error);
    }
    setIsVerifying(false);
    closePrompt();
    refetchOrders();
  };

  const submitUrlEdit = async () => {
    if (activePrompt?.type !== 'edit-url') return;
    const { articleId } = activePrompt;
    const liveUrl = normalizeWebUrl(publishedUrl);
    if (!liveUrl) {
      setPublishedUrlError(INVALID_LIVE_URL_ERROR);
      return;
    }

    setIsVerifying(true);
    try {
      await updateOrderPublishedUrlApi(articleId, workspaceId, liveUrl);
    } catch (error) {
      console.error(error);
    }
    setIsVerifying(false);
    closePrompt();
    refetchOrders();
  };

  const onViewChangeRequest = (row) => {
    setViewChangeRequestRowId(row.id);
    setIsViewChangeRequestSidebarOpen(true);
  };

  const viewChangeRequestRow = orders.find(({ id }) => id === viewChangeRequestRowId) ?? null;

  // The order the "Enter live URL" prompt is for, so the modal can point a
  // link insertion at its brief instead of talking about a published article.
  const addUrlOrder =
    activePrompt?.type === 'add-url'
      ? (orders.find(({ id }) => id === activePrompt.articleId) ?? null)
      : null;
  const isAddUrlForLinkInsertion = isLinkInsertionOrder(addUrlOrder);
  const addUrlBriefUrl = getInsertionBriefUrl(addUrlOrder);

  // If the open row leaves the list (page/filter change, or a refetch that drops it)
  // close the sidebar properly instead of leaving an orphaned isOpen flag that would
  // pop the sidebar back open if the row ever reappeared.
  useEffect(() => {
    if (isViewChangeRequestSidebarOpen && viewChangeRequestRowId != null && !viewChangeRequestRow) {
      setIsViewChangeRequestSidebarOpen(false);
      setViewChangeRequestRowId(null);
    }
  }, [isViewChangeRequestSidebarOpen, viewChangeRequestRowId, viewChangeRequestRow]);

  // Keeps the row chip and the sidebar header in step with the change request the
  // user is looking at, without a full list refetch mid-conversation.
  const onChangeRequestUpdated = (changeRequest: ChangeRequestType) => {
    queryClient.setQueryData<PublishersOrderType[]>(ordersQueryKey, (rows = []) =>
      rows.map((row) =>
        row.optionalChangeRequestId === changeRequest.id
          ? { ...row, optionalChangeRequestStatus: changeRequest.status }
          : row
      )
    );
  };

  // Gated on isLoading, not isFetching: a background refetch used to unmount this
  // whole subtree, including any open sidebar and the reply the user was typing.
  if (isOrdersLoading) {
    return <Loader isLoading withTopMargin />;
  }

  const errorMessage = (ordersError as any)?.message;

  if (errorMessage && errorMessage.indexOf('User is not a member of workspace') !== -1) {
    return <NoAccessMessage message="Oops! You don't have access to this workspace." />;
  }

  if (orders.length === 0) {
    if (!websites || websites.length === 0) {
      const redirectToCampaignCreation = () => {
        redirectUserTo(history, urls.WEBSITE_STEP_ONE(workspaceId, ''));
      };

      return (
        <div
          className="orders-illustration-wrapper"
          data-testid="publisher-orders-empty-no-websites"
        >
          <StubIllustration
            text="Let’s add your first website :)"
            buttonText="Add a new website"
            onClickButton={redirectToCampaignCreation}
            isLoading={isWebsitesFetching}
          />
        </div>
      );
    }
    return (
      <div className="orders-illustration-wrapper" data-testid="publisher-orders-empty">
        <StubIllustration
          text="You’re ready! Orders will appear here soon"
          maxTextSize={340}
          textSize={32}
          // buttonText="Tips to boost orders"
          // No buttonText, so no button renders; onClickButton is required by the prop type.
          onClickButton={() => null}
          buttonType="blue"
          buttonIcon="arrowRightClassic"
          colorIcon="#3424EB"
          buttonSize="xxl"
          IllustrationSVG={OrdersIllustration}
        />
      </div>
    );
  }

  return (
    <>
      <div className="orders-table-wrapper">
        <ConfigurableTable
          config={tableConfig}
          data={orders}
          tableClassName="orders-table"
          rowClassName="orders-table__row"
          testId="publisher-orders-table"
          rowTestId={(row) => `publisher-orders-row-${row.id}`}
          renderProps={{
            onUpdateStatus: handleChangeStatus,
            onEditUrl: handleEditUrl,
            onViewChangeRequest,
          }}
          RenderRow={RenderRow}
        />
      </div>
      <Pagination
        curPage={page}
        onChangePage={changePage}
        pageLimit={limit}
        onChangePageLimit={changeLimit}
        totalItems={orders[0]?.totalItems}
        testId="publisher-orders-pagination"
      />
      <BaseModal
        onClose={closePrompt}
        classNames={{ modal: 'orders-modal-wrapper' }}
        isOpen={activePrompt?.type === 'feedback'}
        testId="publisher-orders-reject-modal"
      >
        <div className="orders-modal__container">
          <div className="orders-modal__header">
            <div className="orders-modal__title">
              <h2>Not quite right? Let us know.</h2>
            </div>
            <p className="orders-modal__subtitle">
              Need tweaks or want to pass? Pick Request changes for minor edits, or Reject{' '}
              {feedbackSubject} to decline it completely.
            </p>
          </div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="orders-modal__label">Feedback</label>
          <Textarea
            placeholder="Please enter your feedback here."
            data-testid="publisher-orders-reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            resizeDisabled
            isFullWidth
          />

          <div className="orders-modal__footer">
            <Button
              disabled={!rejectReason.trim()}
              onClick={() => submitFeedback(PublisherOrderStatus.REVISION_REQUESTED)}
              data-testid="publisher-orders-request-changes-submit"
            >
              Request changes
            </Button>
            <Button
              type="bordered-grey"
              onClick={() => submitFeedback(PublisherOrderStatus.PUBLISHER_ORDER_REJECTED)}
              disabled={!rejectReason.trim()}
              data-testid="publisher-orders-reject-submit"
            >
              Reject {feedbackSubject}
            </Button>
          </div>
        </div>
      </BaseModal>
      <BaseModal
        isOpen={activePrompt?.type === 'add-url'}
        onClose={closePrompt}
        classNames={{ modal: 'orders-modal-wrapper' }}
        testId="publisher-live-url-modal"
      >
        <div className="orders-modal__container">
          <div className="orders-modal__header">
            <div className="orders-modal__title">
              <h2>
                {isAddUrlForLinkInsertion
                  ? 'Enter the live URL of the article with the link'
                  : 'Enter published article URL'}
              </h2>
            </div>
            {addUrlBriefUrl ? (
              <div className="orders-modal__brief">
                <InsertionBrief url={addUrlBriefUrl} {...INSERTION_BRIEF_COPY} />
              </div>
            ) : null}
          </div>
          <div className="orders-modal__content">
            <div className="orders-modal__input-wrapper" data-testid="publisher-live-url-field">
              <Input
                isFullWidth
                onChange={onPublishedUrlChange}
                value={publishedUrl}
                error={publishedUrlError}
                placeholder="Add URL"
                disabled={isVerifying}
                data-testid="publisher-live-url-input"
              />
              <Button
                disabled={!publishedUrl.trim()}
                onClick={submitNewUrl}
                isLoading={isVerifying}
                data-testid="publisher-live-url-submit"
              >
                Verify
              </Button>
            </div>
          </div>
        </div>
      </BaseModal>
      <BaseModal
        isOpen={activePrompt?.type === 'edit-url'}
        onClose={closePrompt}
        classNames={{ modal: 'orders-modal-wrapper' }}
        testId="publisher-edit-url-modal"
      >
        <div className="orders-modal__container">
          <div className="orders-modal__header">
            <div className="orders-modal__title">
              <h2>Edit published URL</h2>
            </div>
            <p className="orders-modal__subtitle">
              Update the live URL for this order. Changes apply immediately.
            </p>
          </div>
          <div className="orders-modal__content">
            <div className="orders-modal__input-wrapper" data-testid="publisher-edit-url-field">
              <Input
                isFullWidth
                onChange={onPublishedUrlChange}
                value={publishedUrl}
                error={publishedUrlError}
                placeholder="Published URL"
                disabled={isVerifying}
                data-testid="publisher-edit-url-input"
              />
              <Button
                disabled={
                  !publishedUrl.trim() ||
                  (activePrompt?.type === 'edit-url' &&
                    publishedUrl.trim() === activePrompt.currentUrl.trim())
                }
                onClick={submitUrlEdit}
                isLoading={isVerifying}
                data-testid="publisher-edit-url-submit"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </BaseModal>
      <ChangeRequestSidebar
        isOpen={isViewChangeRequestSidebarOpen}
        data={viewChangeRequestRow}
        handleClose={() => setIsViewChangeRequestSidebarOpen(false)}
        onChangeRequestUpdated={onChangeRequestUpdated}
      />
    </>
  );
}

export default OrdersTable;
